import { Captura, Dot, Pill, Section } from "./comunes";

export default function AyudaCasos() {
  return (
    <>
      <Section id="casos" title="Casos — la lista">
        <Captura
          src="/ayuda-img/casos.png"
          alt="Lista de casos en escritorio"
          caption="La lista en escritorio: estado, semáforo de actividad, equipo, IMEIs con su propio semáforo y asignado. Arriba, la barra de posesión (Míos / la empresa / Cliente / Proveedor) y el filtro de equipos críticos."
        />
        <ul className="list-disc list-inside space-y-1">
          <li>
            <strong>Buscador</strong> (atajo <code className="text-accent-300">/</code>):
            escribe el <strong>nombre del cliente</strong> o el de su{" "}
            <strong>contacto</strong> (también su correo), el <strong>folio</strong>, el{" "}
            <strong>TE</strong>, el ticket <strong>JIMI</strong>, un <strong>IMEI</strong>,
            la <strong>marca</strong> o el <strong>modelo</strong> del equipo, o cualquier
            palabra de la <strong>descripción</strong>. Busca por pedazos de palabra y no
            distingue mayúsculas ni acentos: <em>pina</em> encuentra a <em>Piña</em>. Para
            dejar fijo un cliente está el filtro aparte (click en el cliente de la fila).
            En el celular el buscador vive dentro de <strong>Filtros</strong>.
          </li>
          <li>
            <strong>+ Nuevo caso</strong>: al guardarlo te lleva{" "}
            <strong>directo al caso</strong> para trabajarlo — no hace falta
            buscarlo en la lista (que puede estar filtrada u ordenada por
            estancados).
          </li>
          <li>
            La columna <strong>Actividad</strong> muestra "hace N días" desde el
            último movimiento (nota o cambio): verde al día, ámbar por
            quemarse, rojo quemado. Los umbrales se configuran en Configuración
            → Automatizaciones → Tiempos y escalación.
          </li>
          <li>
            El menú <strong>Ordenar</strong> acomoda la lista:{" "}
            <em>recién creados</em> (por defecto) o <em>más antiguos</em> por
            fecha de alta, y <strong>más estancados</strong> —los que llevan
            más días sin movimiento hasta arriba, para no perder de vista los
            olvidados— o <em>movidos hace poco</em>. En Histórico solo aplica
            el orden por fecha de creación.
          </li>
          <li>
            La columna <strong>IMEIs</strong> trae cada equipo como etiqueta
            con su semáforo; con más de 3, toca «+N más» para ver el resto ahí
            mismo.
          </li>
          <li>
            <strong>Posesión</strong> ("¿quién tiene la pelota?"): la barra de
            arriba cuenta los casos activos por <strong>Conmigo</strong> /{" "}
            <strong>Cliente</strong> / <strong>Proveedor</strong> y, al hacer
            click, filtra. Los casos que esperan al cliente o al proveedor
            llevan un <strong>badge</strong> (⏳ Cliente / 📦 Proveedor). Se
            deriva del estado del caso.
          </li>
          <li>
            Click en cualquier parte de la fila abre el caso; click en el
            cliente filtra por ese cliente; "Agrupar por cliente" colapsa la
            lista por empresa.
          </li>
          <li>
            <strong>En el celular</strong>: el <strong>buscador está siempre a la
            vista</strong>, arriba de la lista — no hay que abrir nada para
            teclear un folio, un IMEI o el nombre de un cliente. El resto de los
            filtros vive en el botón <strong>Filtros</strong>, y lo que quede
            aplicado se muestra como <strong>fichas debajo</strong>: cada una se
            quita con su ✕ sin volver a abrir el panel, y con{" "}
            <strong>Limpiar todo</strong> se van todas de un toque.
          </li>
          <li>
            <strong>Pasar de caso jalando con el dedo</strong>: dentro de un caso,
            arrastra a los lados para ir al anterior o al siguiente{" "}
            <em>de tu lista</em> (respeta tus filtros y tu orden, igual que las
            flechas «‹ Anterior / Siguiente ›» y el atajo{" "}
            <kbd>Ctrl</kbd>+<kbd>←</kbd>/<kbd>→</kbd>). El caso que se va y el
            que llega <strong>se deslizan pegados</strong>, como las fotos de la
            galería del celular: donde sueltas el dedo es donde arranca el
            deslizamiento. Si de ese lado ya no hay caso, rebota. El scroll de
            arriba a abajo y el de las tablas anchas siguen igual, y el borde
            izquierdo se respeta para no estorbarle al «regresar» del celular.
          </li>
        </ul>
        <Captura
          src="/ayuda-img/casos-movil.png"
          alt="Lista de casos en celular"
          caption="En el celular la lista se vuelve tarjetas con la misma información."
        />
      </Section>

      <Section id="estados" title="Estados y fechas del caso">
        <p className="mb-1">Estados posibles:</p>
        <div>
          <Pill tone="amber">Abierto</Pill>
          <Pill tone="amber">En revisión</Pill>
          <Pill tone="violet">Listo para enviar</Pill>
          <Pill tone="cyan">Enviado al proveedor</Pill>
          <Pill tone="cyan">Esperando proveedor</Pill>
          <Pill tone="sky">Pendiente cliente</Pill>
          <Pill tone="emerald">Cerrado</Pill>
        </div>
        <p className="mt-2">
          <strong>Listo para enviar</strong> es el escalón que faltaba entre revisar
          y enviar: el equipo <strong>ya se diagnosticó</strong>, el proveedor
          autorizó que se lo mandemos, y solo está esperando a que se junte el lote
          de embarque. No es lo mismo que <strong>En revisión</strong> (ahí sí se
          está probando) ni que <strong>Enviado al proveedor</strong> (ahí ya salió).
          Filtrando por él ves el lote acumulándose, y cuando ya sean suficientes se
          van todos juntos. Su <strong>SLA arranca en 20/30 días</strong> —no en los
          3/7/10 de los demás— porque la espera es una decisión, no un descuido; se
          ajusta en Configuración → Automatizaciones → Tiempos y escalación.
        </p>
        <p className="mt-2">
          Cada caso tiene un panel de <strong>fechas operativas</strong> (todas
          opcionales): reporte del cliente (ancla del SLA), formulario,
          recepción en oficina, envío y retorno del proveedor, devolución al
          cliente y reemplazo por garantía. El switch <strong>Fue garantía</strong>{" "}
          marca la unidad para los reportes aunque el reemplazo siga en proceso.
        </p>
        <p className="mt-2">
          <strong>Las fechas RMA están vivas</strong>: al llenar una, el caso se
          mueve solo al estado que toca y sus equipos también (Recepción → En
          revisión; Envío al proveedor → Con proveedor; Retorno → En revisión;
          Devolución → OK), <strong>respetando los equipos en CRIT</strong>. Además
          se programa el <strong>seguimiento</strong> que aplica (p. ej. preguntar
          al proveedor ~1 semana después del envío) y se cancela el de la etapa
          anterior. Todo queda en la bitácora y lo puedes ajustar a mano (estado,
          equipos o la pestaña Recordatorios). Prender <strong>Fue garantía</strong>
          pone la fecha de reemplazo de hoy y pasa el caso a Garantía; apagarlo
          conserva la fecha.
        </p>
      </Section>

      <Section id="cerrar-caso" title="Cerrar un caso y la solución">
        <p>
          Al <strong>cerrar un caso</strong> —ya sea con el botón{" "}
          <strong>Cerrar caso</strong> o mandando un correo de cierre con{" "}
          <strong>Cerrar caso al enviar</strong> activado— ahora es{" "}
          <strong>obligatorio indicar el tipo de solución</strong>: cómo se
          resolvió el caso. Sin elegirla, el botón de cierre se queda
          deshabilitado.
        </p>
        <p className="mt-2">
          La solución se elige de una <strong>lista</strong>. Por ejemplo:
        </p>
        <div className="mt-1">
          <Pill tone="emerald">Reparación</Pill>
          <Pill tone="emerald">Reemplazo</Pill>
          <Pill tone="emerald">Cambio por garantía</Pill>
        </div>
        <ul className="mt-2 list-disc list-inside space-y-1">
          <li>
            Si la solución que necesitas <strong>no está en la lista</strong>,
            puedes <strong>crear una nueva al vuelo</strong> sin salir del
            cierre y queda disponible para la próxima.
          </li>
          <li>
            Si el caso tiene <strong>varios equipos</strong>, la solución que
            elijas se aplica <strong>a todos por default</strong>; pero puedes{" "}
            <strong>elegir una distinta por equipo</strong> cuando cada IMEI se
            resolvió de forma diferente.
          </li>
        </ul>
        <p className="mt-2 text-zinc-400">
          Esta información alimenta las estadísticas{" "}
          <strong>«¿Cómo se resuelven?»</strong>, así que entre más fiel sea la
          solución que marques, más útiles salen los reportes.
        </p>
      </Section>

      <Section id="detalle" title="Dentro del caso">
        <Captura
          src="/ayuda-img/caso-detalle.png"
          alt="Detalle de un caso"
          caption="El encabezado: contactos con sus acciones (★ rol, ✎ editar, ✕ quitar), estado, asignado y descripción."
        />
        <ul className="list-disc list-inside space-y-1">
          <li>
            <strong>Clic para copiar</strong>: el <strong>folio</strong>, el{" "}
            <strong>TE</strong> y el <strong>ticket de Jimi</strong> del
            encabezado se copian al portapapeles con un solo clic — ya no hay
            que seleccionarlos a mano. Te avisa con un ✓ y un mensajito. Los{" "}
            <strong>IMEIs</strong> de la franja "Equipos" hacen lo mismo.
          </li>
          <li>
            <strong>Contactos</strong>: el principal recibe el correo (Para);
            los de seguimiento van en copia. Cada contacto trae sus acciones al
            lado: ★ cambia el rol, ✎ edita sus datos (nombre, correo,
            teléfono — el cambio aplica a la ficha del cliente, se ve en
            todos sus casos) y ✕ lo quita del caso (pide confirmación).
          </li>
          <li>
            <strong>‹ Anterior · Siguiente ›</strong>: arriba a la derecha,
            para recorrer los casos <strong>en el orden de tu lista</strong>{" "}
            (con tus filtros y orden, p. ej. "más estancados") sin regresar a
            ella. Atajos: <strong>Ctrl+←</strong> y <strong>Ctrl+→</strong> (las
            flechas solas son del visor de evidencias). Los tres caminos —dedo,
            botón y atajo— <strong>se ven igual</strong> (el mismo deslizamiento)
            y el caso nuevo <strong>abre desde arriba</strong>, no a media página
            del anterior. Si entras por un link directo (alerta, TE) no aparecen
            — no hay lista de referencia.
          </li>
          <li>
            <strong>▾ Equipos (N)</strong>: franja arriba de las pestañas con
            los IMEIs del caso y su semáforo, para tenerlos a la vista
            mientras escribes en la bitácora sin cambiar de pestaña. Clic en
            un equipo <strong>copia su IMEI</strong>; el ▸ la oculta y se
            recuerda tu preferencia. El tab IMEIs conserva todo lo demás
            (estados, ficha, fechas).
          </li>
          <li>
            <strong>Pestañas</strong>: Bitácora interna (NO se manda al
            cliente), IMEIs, Evidencias, Correos y Recordatorios.
          </li>
          <li>
            <strong>Paquetes ZIP de evidencias</strong>: arriba de la pestaña
            Evidencias, la tarjeta <strong>Paquetes generados</strong> lista
            cada ZIP del caso con <strong>cuándo se generó</strong> (fecha y
            hora: si regeneras el mismo día, la hora es lo que los distingue),
            su tamaño, cuántas veces se descargó (✓ si ya lo bajaron) y cuándo
            expira su enlace. La contraseña del caso va una sola vez arriba.
            Con el <strong>▾</strong> del título la tarjeta se{" "}
            <strong>pliega</strong> para que no estorbe mientras subes
            archivos; plegada sigue mostrando la contraseña y el último ZIP, y
            recuerda cómo la dejaste. Al <strong>generar un ZIP nuevo</strong>{" "}
            se abre sola, para que el paquete recién hecho no quede escondido.
          </li>
        </ul>
        <Captura
          src="/ayuda-img/paquetes-zip.png"
          alt="Tarjeta Paquetes generados con tres ZIPs, cada uno con su fecha y hora de generación, tamaño y descargas"
          caption="Paquetes generados: cada ZIP dice cuándo se generó; el ▾ del título pliega la tarjeta y la preferencia se recuerda."
        />
        <ul className="list-disc list-inside space-y-1">
          <li>
            <strong>Entradas del sistema</strong>: además de tus notas, la
            bitácora registra solita los eventos del caso (creación, cambios,
            cierre, correos, IMEIs). Se leen en lenguaje claro y, en los
            cambios, muestran <em>de qué valor a qué valor</em> (por ejemplo
            «Estado: Abierto → Cerrado; Asignado a: — → Warren»). No se borra
            ni se oculta nada.
          </li>
          <li>
            <strong>Tipo de falla</strong>: al crear un caso con categoría
            "Falla", elige el tipo del catálogo — alimenta el reporte de fallas
            recurrentes del Panel.
          </li>
          <li>
            <strong>Recordatorio desde la bitácora</strong>: al escribir una
            nota puedes activar <strong>⏰ Recordatorio</strong> y elegir
            cuándo (en 1/2/4 horas, mañana 9:00 o una fecha exacta). Se crea
            junto con la nota, usa su texto y le avisa a quien abrió el caso y al asignado; la
            nota queda marcada con un badge. Lo editas o cancelas después en
            la pestaña <strong>Recordatorios</strong>.
          </li>
          <li>
            <strong>Fotos de constancia</strong>: con el botón{" "}
            <strong>📎 Fotos</strong> (junto a «Registrar llamada») eliges
            imágenes, o las <strong>arrastras a cualquier parte</strong> del
            recuadro donde escribes; y con <strong>🖼️ De Evidencias</strong>{" "}
            citas una foto que <strong>ya está en el caso</strong> sin volver a
            subirla (puedes elegir varias). Quedan colgadas de esa nota — sirve para
            dejar constancia de lo que no es
            texto: la captura de un mensaje del cliente, una foto de cómo llegó
            el equipo. Cada foto se ve como miniatura en la nota y se abre en el{" "}
            <strong>mismo visor</strong> que las evidencias (flechas, teclado y
            deslizar). Tres cosas que conviene saber:{" "}
            <strong>solo imágenes</strong> (el video y los documentos van por la
            pestaña Evidencias); la foto{" "}
            <strong>también queda en Evidencias</strong>, marcada como
            «bitácora», así que no se pierde aunque cambies de idea con la nota;
            y como es material interno,{" "}
            <strong>no se preselecciona al armar el ZIP del proveedor</strong>{" "}
            (puedes marcarla a mano si de verdad se la quieres mandar). Al pasar
            los 18 meses de retención, la nota muestra «archivo eliminado» en su
            lugar.
            <Captura
              src="/ayuda-img/bitacora-fotos.png"
              alt="Compositor de la bitácora con una foto de constancia elegida"
              caption="El compositor: «📎 Fotos» sube nuevas (o arrástralas), «🖼️ De Evidencias» cita una que ya está en el caso; la miniatura queda colgada de la nota."
            />
          </li>
          <li>
            <strong>Comentarios por equipo y filtros</strong>: al anotar en la
            bitácora puedes asociar la nota a un <strong>IMEI</strong> (o dejarla
            "General del caso"); las notas con equipo muestran un{" "}
            <strong>chip IMEI</strong>. Arriba de la bitácora puedes{" "}
            <strong>filtrar</strong> por equipo y por lo que es cada nota:{" "}
            <strong>Conversación</strong> deja todo lo que es hablar con alguien
            — tus notas, el ida y vuelta con el proveedor en su ticket y los
            correos que entran y salen —, mientras que{" "}
            <strong>Eventos del sistema</strong> deja los cambios del expediente
            (estados, fechas, equipos, apertura y cierre).
          </li>
          <li>
            <strong>Cambiar el estado del equipo desde la bitácora</strong>: al
            escribir una nota puedes fijar un nuevo estado del IMEI de dos formas
            — escribiendo <kbd>/</kbd> en la nota (menú rápido) o con el botón{" "}
            <strong>± Cambiar estado del equipo</strong>. Si la nota es de un{" "}
            <strong>IMEI</strong> concreto, cambia solo ese; si es{" "}
            <strong>General del caso</strong>, cambia <strong>todos</strong> (los
            marcados <strong>CRIT</strong> se respetan). El comentario queda como
            justificación y aparece un <strong>Deshacer</strong>. Aparte, el botón{" "}
            <strong>± Cambiar estado del caso</strong> (separado del equipo) fija el{" "}
            <strong>estado del caso</strong> sin tocar los equipos — útil para poner{" "}
            <strong>Esperando proveedor</strong> (esperas su respuesta y el equipo
            sigue contigo), distinto de marcar el equipo{" "}
            <strong>Con proveedor</strong>, que significa que lo{" "}
            <strong>enviaste físicamente</strong> (el caso pasa a{" "}
            <strong>Enviado al proveedor</strong>). En consultas sin IMEI, el{" "}
            <kbd>/</kbd> también ofrece los estados del caso.
          </li>
          <li>
            <strong>Mencionar a un compañero (@)</strong>: en la bitácora
            escribe <strong>@</strong> y elige a quién avisarle. Queda como un
            <strong>pendiente que no desaparece</strong> hasta que lo atiendan:
            a esa persona le aparece en su bandeja <em>"Esperan mi respuesta"</em>
            (con contador en la barra) y el caso muestra un badge{" "}
            <em>"⏳ te esperan"</em>. Se resuelve solo cuando esa persona{" "}
            <strong>da seguimiento</strong> en el caso: escribe una nota,{" "}
            <strong>le responde al proveedor</strong> en su ticket o{" "}
            <strong>manda un correo</strong> del caso. También con los botones{" "}
            <strong>Atender</strong> / <strong>Descartar</strong>. Si solo la
            abriste sin responder, el badge se pone <strong>gris</strong>: sigue
            pendiente, pero deja de gritar.
          </li>
        </ul>
      </Section>

      <Section id="semaforo-imei" title="Semáforo de IMEIs">
        <ul className="space-y-1">
          <li><Dot color="emerald" /> <strong>OK</strong>: sin acción pendiente.</li>
          <li><Dot color="amber" /> <strong>REV</strong>: en revisión activa.</li>
          <li><Dot color="red" /> <strong>CRIT</strong>: 7+ días sin movimiento.</li>
          <li><Dot color="sky" /> <strong>CLI</strong>: en espera del cliente.</li>
          <li><Dot color="orange" /> <strong>PROVEEDOR</strong>: en manos del proveedor / fabricante.</li>
        </ul>
        <p className="mt-2 text-zinc-400">
          Sin estado puesto a mano, el semáforo envejece solo: menos de 4 días
          sin movimiento = OK, de 4 a 6 = REV, 7 o más = CRIT. En un caso{" "}
          <strong>cerrado</strong> todos los equipos se ven en <strong>OK</strong>{" "}
          y ya no cuentan días: no queda nada que hacer ahí (si el caso se
          reabre, cada equipo vuelve al estado que tenía).
        </p>
        <p className="mt-2 text-zinc-400">
          <strong>Agregar, editar o quitar</strong> IMEIs de un caso{" "}
          <strong>abierto</strong> se hace en su pestaña <strong>IMEIs</strong>:
          el botón <strong>Agregar IMEI</strong> abre un modal (acepta varios
          separados por coma, espacio o salto de línea —ideal para pegar desde
          Excel—; el equipo se asume del <strong>modelo del caso</strong>), y cada equipo tiene "Editar" / "Quitar". Si el caso
          todavía no tiene ninguno, aparece también el atajo{" "}
          <strong>+ Agregar IMEI</strong> en el encabezado. En casos{" "}
          <strong>cerrados</strong> los IMEIs son de solo lectura: para un
          equipo nuevo usa <strong>+ Caso relacionado</strong>.
        </p>
        <p className="mt-2 text-zinc-400">
          El estado se cambia con <strong>botones de color</strong> (OK, REV,
          CLI, PROVEEDOR, CRIT) en cada equipo; <strong>Auto</strong> quita el
          estado manual y deja que el semáforo envejezca solo. Cada equipo tiene
          además una <strong>ficha de "Datos de referencia"</strong> editable
          (tel. del cliente, contacto en sitio…) — es solo de ese equipo, no es
          evidencia ni bitácora.
        </p>
        <p className="mt-2 text-zinc-400">
          <strong>CRIT con prioridad</strong>: los equipos en CRÍTICO suben al
          tope de la pestaña IMEIs, el caso muestra un <strong>badge rojo
          «⚠ crítico»</strong> en el listado (con filtro <em>«Solo con equipo
          crítico»</em>), y al marcarlo se avisa al asignado. En el panel de
          fechas, un indicador <strong>«📦 Equipo contigo»</strong> recuerda que
          tienes la unidad, y un aviso señala si las fechas quedaron fuera de orden.
        </p>
      </Section>

      <Section id="hilos" title="Hilos de casos (caso relacionado)">
        <p>
          Un caso cerrado <strong>no se reabre</strong>. Si el cliente vuelve
          con el mismo equipo, usa <strong>+ Caso relacionado</strong> desde el
          caso cerrado: abre Nuevo caso con cliente, equipo e IMEIs
          precargados, y ambos quedan enlazados en el panel "Hilo del caso".
        </p>
      </Section>

      <Section id="duplicados" title="Casos duplicados (mismo IMEI)">
        <p>
          Al crear un caso, si algún <strong>IMEI</strong> ya está en otro caso
          abierto (o cerrado hace poco), aparece un aviso ámbar con el{" "}
          <strong>link al caso existente</strong>. No bloquea: si es el mismo
          problema, ábrelo; si es otro, continúa. Al dar <strong>Crear</strong>{" "}
          también sale una confirmación.
        </p>
        <p className="mt-2">
          El mismo espíritu aplica al <strong>catálogo de equipos</strong>: si
          el modelo del caso no está en el catálogo —porque el cliente lo
          escribió a su manera— el selector muestra los{" "}
          <strong>parecidos que ya existen</strong>, con quién agregó cada uno
          y un <strong>Usar este</strong> que lo elige de un clic. Solo debajo
          queda <strong>Agregarlo</strong>, por si de verdad es un equipo
          nuevo. El mismo aviso sale al crear un modelo desde Configuración →
          Catálogos.
        </p>
        <p className="mt-2 text-zinc-400">
          Dos detalles del selector: las variantes de escritura se resuelven
          solas (quien escribe <strong>LL303PRO</strong> queda amarrado a{" "}
          <strong>LL303 Pro</strong>), pero un nombre <em>ambiguo</em> —que se
          parece a dos equipos distintos— se deja marcado como fuera de
          catálogo a propósito: mejor que lo decida una persona a que el
          sistema adivine mal en silencio. Y un modelo{" "}
          <strong>descontinuado</strong> ya no se ofrece para casos nuevos,
          pero sigue apareciendo (marcado como tal) en los casos que ya lo
          traían, para no perder de qué equipo se trataba.
        </p>
        <p className="mt-2 text-zinc-400">
          Si ya se creó un duplicado, en el caso hay un botón{" "}
          <strong>Marcar como duplicado</strong>: sugiere los casos que
          comparten IMEI (o búscalo por <strong>folio o TE</strong>); al
          elegir el original, este caso se{" "}
          <strong>relaciona y se cierra</strong> como duplicado (queda un banner
          🔗 con link al original). Es <strong>reversible</strong> con{" "}
          <strong>Quitar marca</strong> (reabre el caso si lo habíamos cerrado
          como duplicado). No se borran datos ni se mezclan bitácoras.
        </p>
      </Section>
    </>
  );
}
